import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const CHIPS = [
  { label: "All", value: "" },
  { label: "Pre-Clinical", value: "Pre-Clinical" },
  { label: "Para-Clinical", value: "Para-Clinical" },
  { label: "Clinical", value: "Clinical" },
  { label: "Active", value: "ACTIVE" },
];

export default function Directory() {
  const navigate = useNavigate();
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [chip, setChip] = useState("");
  const [pinModal, setPinModal] = useState(null); // { faculty, action: 'delete'|'archive' }
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinLoading, setPinLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined };
      if (chip === "ACTIVE") params.status = "Active";
      else if (chip) params.dept_type = chip;
      const { data } = await api.get("/faculty", { params });
      setFaculty(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chip]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const initialsOf = (n) => (n || "?").split(" ").filter(Boolean).map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const total = faculty.length;

  const openPinModal = (f, action) => {
    setPinModal({ faculty: f, action });
    setPin("");
    setPinError("");
  };

  const executePinAction = async () => {
    if (!pin.trim()) { setPinError("Enter PIN"); return; }
    setPinLoading(true);
    setPinError("");
    try {
      const fid = pinModal.faculty.faculty_id;
      if (pinModal.action === "delete") {
        await api.post(`/faculty/${fid}/delete`, { pin });
      } else {
        await api.post(`/faculty/${fid}/archive`, { pin });
      }
      setPinModal(null);
      load();
    } catch (e) {
      setPinError(e?.response?.data?.detail || "Action failed");
    } finally { setPinLoading(false); }
  };

  return (
    <div data-testid="view-directory">
      <div className="topbar">
        <div>
          <h1 className="pg-title">Faculty Directory</h1>
          <div className="pg-sub">{total} faculty across 18 departments · click any card to view profile</div>
        </div>
        <div className="topbar-meta"><span className="meta-chip">AY 2025–26</span></div>
      </div>

      <div className="search-row">
        <div className="search-box">
          <span style={{color:'var(--text-3)'}}>&#8981;</span>
          <input
            placeholder="Search by name, Aadhaar, AEBAS ID, department, designation…"
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="directory-search"
          />
        </div>
        <div className="chip-row">
          {CHIPS.map(c => (
            <div
              key={c.label}
              className={`chip ${chip === c.value ? 'active' : ''}`}
              onClick={() => setChip(c.value)}
              data-testid={`filter-chip-${c.label.toLowerCase()}`}
            >{c.label}</div>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{padding:40, textAlign:'center'}}><div className="spinner"/></div>
      ) : (
        <div className="faculty-grid" data-testid="faculty-grid">
          {faculty.map(f => (
            <div
              key={f.faculty_id}
              className="fac-card"
              onClick={() => navigate(`/profile/${f.faculty_id}`)}
              data-testid={`faculty-card-${f.ssmch_id}`}
            >
              <div className="fac-head">
                <div className="fac-av">{initialsOf(f.full_name)}</div>
                <div style={{flex:1}}>
                  <div className="fac-name">{f.title} {f.full_name}</div>
                  <div className="fac-id">{f.ssmch_id}</div>
                </div>
                <div style={{display:'flex',gap:4,flexShrink:0}}>
                  <button
                    className="btn"
                    style={{fontSize:11,padding:'4px 10px'}}
                    onClick={e => { e.stopPropagation(); navigate(`/edit/${f.faculty_id}`); }}
                    data-testid={`edit-btn-${f.ssmch_id}`}
                  >Edit</button>
                  <button
                    className="btn"
                    style={{fontSize:11,padding:'4px 10px',color:'#b45309'}}
                    onClick={e => { e.stopPropagation(); openPinModal(f, 'archive'); }}
                    data-testid={`archive-btn-${f.ssmch_id}`}
                  >Archive</button>
                  <button
                    className="btn"
                    style={{fontSize:11,padding:'4px 10px',color:'#dc2626'}}
                    onClick={e => { e.stopPropagation(); openPinModal(f, 'delete'); }}
                    data-testid={`delete-btn-${f.ssmch_id}`}
                  >Delete</button>
                </div>
              </div>
              <div className="fac-meta">
                <div><div className="lbl">Department</div><div className="val sans">{f.department}</div></div>
                <div><div className="lbl">Designation</div><div className="val sans">{f.designation}</div></div>
                <div><div className="lbl">Status</div><div><span className={`badge ${f.status === 'Active' ? 'ok' : f.status === 'Absconded' || f.status === 'Archived' ? 'bad' : 'warn'}`}>{f.status}</span></div></div>
                <div><div className="lbl">Track</div><div className="val sans">{f.track}</div></div>
              </div>
            </div>
          ))}
          <div className="fac-card cta" onClick={() => navigate("/add")} data-testid="add-faculty-cta">
            <div className="plus">+</div>
            <div style={{fontWeight:500, fontSize:13}}>Add New Faculty</div>
          </div>
        </div>
      )}

      {/* PIN Modal */}
      {pinModal && (
        <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.4)'}} onClick={() => setPinModal(null)} data-testid="pin-modal-overlay">
          <div style={{background:'var(--bg)',border:'1px solid var(--border)',borderRadius:14,padding:'28px 32px',minWidth:340,maxWidth:420,boxShadow:'0 16px 48px rgba(0,0,0,.15)'}} onClick={e => e.stopPropagation()} data-testid="pin-modal">
            <div style={{fontSize:16,fontWeight:600,marginBottom:4}}>
              {pinModal.action === 'delete' ? 'Delete Faculty Record' : 'Archive Faculty Record'}
            </div>
            <div style={{fontSize:13,color:'var(--text-2)',marginBottom:16}}>
              {pinModal.action === 'delete'
                ? <>This will <strong style={{color:'#dc2626'}}>permanently delete</strong> <strong>{pinModal.faculty.title} {pinModal.faculty.full_name}</strong> ({pinModal.faculty.ssmch_id}) and all associated life events. This cannot be undone.</>
                : <>This will set <strong>{pinModal.faculty.title} {pinModal.faculty.full_name}</strong> ({pinModal.faculty.ssmch_id}) status to <strong style={{color:'#b45309'}}>Archived</strong>. The record will be preserved but marked inactive.</>
              }
            </div>
            <div style={{marginBottom:12}}>
              <label style={{fontSize:12,fontWeight:500,display:'block',marginBottom:4}}>Enter PIN to confirm</label>
              <input
                type="password"
                className="form-input mono"
                placeholder="Enter 4-digit PIN"
                maxLength={4}
                autoFocus
                value={pin}
                onChange={e => { setPin(e.target.value.replace(/[^0-9]/g,'')); setPinError(""); }}
                onKeyDown={e => { if (e.key === 'Enter') executePinAction(); }}
                style={{textAlign:'center',fontSize:18,letterSpacing:8}}
                data-testid="pin-input"
              />
            </div>
            {pinError && <div style={{color:'#dc2626',fontSize:12,marginBottom:8}} data-testid="pin-error">{pinError}</div>}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn" style={{fontSize:12}} onClick={() => setPinModal(null)} data-testid="pin-cancel">Cancel</button>
              <button
                className="btn"
                style={{fontSize:12,padding:'6px 18px',background:pinModal.action==='delete'?'#dc2626':'#b45309',color:'#fff',border:'none',borderRadius:6}}
                disabled={pinLoading || pin.length < 4}
                onClick={executePinAction}
                data-testid="pin-confirm"
              >{pinLoading ? 'Processing…' : pinModal.action === 'delete' ? 'Delete' : 'Archive'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
